<template>
    <div class="post-list">
        <h2 v-if="showHeading" class="post-list__heading">
            <slot />

            <LocalizedLink v-if="viewAllLink" class="post-list__view-all" :to="viewAllLink">
                {{ $t('magazine.view_all') }}
                <svg-icon name="keyboard_arrow_right" />
            </LocalizedLink>
        </h2>
        <LoadingIndicator v-if="$fetchState.pending" />
        <!-- Posts -->
        <template v-else-if="posts.length > 0">
            <div class="post-list__items">
                <PostCard
                    v-for="post in posts"
                    :key="post.id"
                    :post="post"
                    :settings="settings || undefined"
                    class="post-list__item"
                />
            </div>
            <Pagination
                v-if="showPagination"
                :current-page="currentPage"
                :total-pages="totalPages"
                class="post-list__pagination"
                @page-change="handlePageChange"
            />
        </template>
        <!-- No posts -->
        <template v-else>
            <div class="post-list__no-posts">
                <svg-icon name="search" class="post-list__no-posts-icon" />
                <div>{{ $t('magazine.no_posts') }}</div>
            </div>
        </template>
    </div>
</template>

<script>
import LoadingIndicator from '~/components/LoadingIndicator.vue';
import PostCard from '~/components/magazine/PostCard.vue';
import Pagination from '~/components/Pagination.vue';

export default {
    components: { LoadingIndicator, PostCard, Pagination },
    props: {
        receivedPosts: {
            type: Array,
            default: () => [],
        },
        viewAllLink: {
            type: [String, Object],
            default: null,
        },
        ids: {
            type: [String, Number],
            default: null,
        },
        settings: {
            type: Object,
            default: null,
        },
        showPagination: {
            type: Boolean,
            default: true,
        },
        excludeIds: {
            type: String,
            default: '',
        },
        taxonomyTerm: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            posts: this.receivedPosts,
            totalPages: 0,
            currentPage: parseInt(this.$route.query.page) || 1,
        };
    },
    async fetch() {
        try {
            if (this.ids !== null && this.taxonomyTerm !== null) {
                const result = await this.$cmsService.getPostsForTaxonomy(
                    this.taxonomyTerm,
                    this.ids,
                    this.currentPage,
                    this.excludeIds
                );
                this.posts = result.posts;
                this.totalPages = parseInt(result.meta.totalPages);
            }
        } catch (err) {
            this.posts = [];
            this.$sentry.captureException(err);
        }
    },
    computed: {
        showHeading() {
            return this.$slots.default !== undefined || this.viewAllLink !== null;
        },
    },
    methods: {
        async handlePageChange(pageNumber) {
            this.currentPage = pageNumber;
            await this.$fetch();
            this.$router.push({
                query: {
                    page: this.currentPage,
                },
            });
            this.$emit('page-change', pageNumber);
        },
    },
};
</script>

<style lang="scss">
@import '@/sass/_variables.scss';

$post-item-gutter: 30px;

.post-list {
    &__heading {
        @extend %heading-3;
        padding-bottom: $md-magazine-spacing;
        position: relative;

        @include md {
            padding-bottom: $lg-magazine-spacing;
        }
    }

    &__view-all {
        position: absolute;
        right: 0;

        .svg-icon {
            margin-bottom: 2px; // Align vertically
        }
    }

    &__items {
        display: flex;
        flex-wrap: wrap;
        gap: $post-item-gutter;

        @include lg {
            margin-right: -$post-item-gutter;
        }
    }

    &__item {
        width: 100%;

        @include lg {
            width: calc(100% / 3 - #{$post-item-gutter});
        }
    }

    &__pagination {
        justify-content: center;
        margin-top: $lg-magazine-spacing;
    }

    &__no-posts {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }

    &__no-posts-icon {
        width: 50px;
        height: 50px;
        margin-bottom: 20px;
    }

    .post-group {
        &__item {
            border-bottom: 0;
            padding: 0;
        }
    }
}
</style>
